import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { db } from './firebase';
import { doc, getDoc, getDocs, collection, query, where } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { getInitials } from './utils';

export default function FollowersModal({ isOpen, onClose, userId, type, userDisplayName }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && userId) {
      fetchUsers();
    }
  }, [isOpen, userId, type]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists()) {
        const userData = userDoc.data();
        const userIds = type === 'followers' ? (userData.followers || []) : (userData.following || []);

        if (userIds.length === 0) {
          setUsers([]);
          setLoading(false);
          return;
        }

        // Fetch user details for each userId
        const userDetails = await Promise.all(
          userIds.map(async (uid) => {
            try {
              const userDetailDoc = await getDoc(doc(db, 'users', uid));
              if (userDetailDoc.exists()) {
                return {
                  id: uid,
                  ...userDetailDoc.data()
                };
              }
              return null;
            } catch (error) {
              console.error(`Error fetching user ${uid}:`, error);
              return null;
            }
          })
        );

        setUsers(userDetails.filter(user => user !== null));
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUserClick = (clickedUserId) => {
    onClose();
    navigate(`/utente/${clickedUserId}`, {
      state: {
        fromPage: window.location.pathname,
        scrollY: window.scrollY
      }
    });
  };

  if (!isOpen) return null;

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h3 style={styles.title}>
            {type === 'followers' 
              ? `${t('followers.followers')} ${userDisplayName}`
              : `${t('followers.following')} ${userDisplayName}`
            }
          </h3>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div style={styles.content}>
          {loading ? (
            <div style={styles.loading}>{t('common.loading')}</div>
          ) : users.length === 0 ? (
            <div style={styles.empty}>
              {type === 'followers' 
                ? t('followers.noFollowers')
                : t('followers.noFollowing')
              }
            </div>
          ) : (
            <div style={styles.userList}>
              {users.map((user) => (
                <div 
                  key={user.id} 
                  style={styles.userItem}
                  onClick={() => handleUserClick(user.id)}
                >
                  <div style={styles.avatar}>
                    {user.avatar ? (
                      <img src={user.avatar} alt={user.displayName} style={styles.avatarImage} />
                    ) : (
                      <div style={styles.avatarFallback}>
                        {getInitials(user.displayName)}
                      </div>
                    )}
                  </div>
                  <div style={styles.userInfo}>
                    <div style={styles.userName}>{user.displayName || 'Utente'}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    zIndex: 9999,
    fontFamily: 'Poppins, sans-serif'
  },
  modal: {
    position: 'fixed',
    top: '15%',
    left: '50%',
    transform: 'translate(-50%, 0)',
    backgroundColor: '#fff',
    borderRadius: '16px',
    width: '90%',
    maxWidth: '400px',
    maxHeight: '70vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
    zIndex: 10000
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 20px 16px 20px',
    borderBottom: '1px solid #eee'
  },
  title: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#111',
    margin: 0,
    flex: 1
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    color: '#666',
    padding: '4px',
    marginLeft: '12px'
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    padding: '0 20px 20px 20px'
  },
  loading: {
    textAlign: 'center',
    padding: '40px 20px',
    color: '#666',
    fontSize: '14px'
  },
  empty: {
    textAlign: 'center',
    padding: '40px 20px',
    color: '#999',
    fontSize: '14px'
  },
  userList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    paddingTop: '16px'
  },
  userItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    border: '1px solid #f0f0f0'
  },
  avatar: {
    width: '45px',
    height: '45px',
    flexShrink: 0
  },
  avatarImage: {
    width: '100%',
    height: '100%',
    borderRadius: '50%',
    objectFit: 'cover'
  },
  avatarFallback: {
    width: '100%',
    height: '100%',
    borderRadius: '50%',
    backgroundColor: '#000',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
    fontWeight: '600'
  },
  userInfo: {
    flex: 1,
    minWidth: 0
  },
  userName: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#111',
    marginBottom: '2px'
  },
  
};